import React, { useState, useEffect } from 'react';
import { Upload, X, Image, Trash2, Check, Plus, Edit2, Calendar, ChevronLeft, Download } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AlbumsManagement = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [downloadingAlbum, setDownloadingAlbum] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    date_album: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/albums`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAlbums(data.albums || []);
      }
    } catch (error) {
      console.error('Erreur chargement albums:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/albums`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const newAlbum = await response.json();
        setAlbums([newAlbum, ...albums]);
        setFormData({ titre: '', date_album: '' });
        setShowCreateForm(false);
        alert('Album créé avec succès !');
      } else {
        alert('Erreur lors de la création de l\'album');
      }
    } catch (error) {
      console.error('Erreur création album:', error);
      alert('Erreur lors de la création de l\'album');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleUploadPhotos = async () => {
    if (!selectedAlbum || selectedFiles.length === 0) return;

    setUploadingPhotos(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_BASE}/api/admin/albums/${selectedAlbum.id}/photos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        alert(`${selectedFiles.length} photo(s) ajoutée(s) avec succès !`);
        setSelectedFiles([]);
        fetchAlbumDetails(selectedAlbum.id);
      } else {
        alert('Erreur lors de l\'upload des photos');
      }
    } catch (error) {
      console.error('Erreur upload photos:', error);
      alert('Erreur lors de l\'upload des photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const fetchAlbumDetails = async (albumId) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/albums/${albumId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const album = await response.json();
        setSelectedAlbum(album);
        // Mettre à jour aussi dans la liste
        setAlbums(prev => prev.map(a => a.id === albumId ? album : a));
      }
    } catch (error) {
      console.error('Erreur chargement album:', error);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!selectedAlbum || !window.confirm('Supprimer cette photo ?')) return;

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/albums/${selectedAlbum.id}/photos/${photoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Photo supprimée avec succès');
        fetchAlbumDetails(selectedAlbum.id);
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression photo:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSetCover = async (photoId) => {
    if (!selectedAlbum) return;

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/albums/${selectedAlbum.id}/cover/${photoId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Photo de couverture mise à jour');
        fetchAlbumDetails(selectedAlbum.id);
      } else {
        alert('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur mise à jour couverture:', error);
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    if (!window.confirm('Supprimer cet album et toutes ses photos ?')) return;

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/albums/${albumId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Album supprimé avec succès');
        setAlbums(prev => prev.filter(a => a.id !== albumId));
        if (selectedAlbum?.id === albumId) {
          setSelectedAlbum(null);
        }
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression album:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleDownloadAlbum = async (albumId, albumTitle) => {
    setDownloadingAlbum(albumId);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/albums/${albumId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const imagesCount = response.headers.get('X-Images-Count') || '?';
        const totalPhotos = response.headers.get('X-Total-Photos') || '?';
        
        // Créer un lien de téléchargement
        const safeTitle = albumTitle.replace(/[^a-zA-Z0-9àâäéèêëïîôùûüç\s-_]/g, '').replace(/\s+/g, '_');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${safeTitle}_photos.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        
        alert(`✅ ${imagesCount}/${totalPhotos} photos téléchargées avec succès !`);
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur téléchargement album:', error);
      alert(`❌ Erreur: ${error.message}`);
    } finally {
      setDownloadingAlbum(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">
      <div className="text-xl">Chargement...</div>
    </div>;
  }

  // Vue détaillée d'un album
  if (selectedAlbum) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={() => setSelectedAlbum(null)}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Retour aux albums
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-3xl font-bold text-gray-800">{selectedAlbum.titre}</h2>
              <p className="text-gray-600 mt-1">
                <Calendar className="inline w-4 h-4 mr-1" />
                {new Date(selectedAlbum.date_album).toLocaleDateString('fr-FR')}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {selectedAlbum.photos?.length || 0} photo(s)
              </p>
            </div>
            <div className="flex gap-2">
              {selectedAlbum.photos?.length > 0 && (
                <button
                  onClick={() => handleDownloadAlbum(selectedAlbum.id, selectedAlbum.titre)}
                  disabled={downloadingAlbum === selectedAlbum.id}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloadingAlbum === selectedAlbum.id ? 'Téléchargement...' : 'Télécharger tout (ZIP)'}
                </button>
              )}
              <button
                onClick={() => handleDeleteAlbum(selectedAlbum.id)}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer l'album
              </button>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold mb-3">Ajouter des photos</h3>
            <div className="flex items-center gap-4">
              <input
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileSelect}
                className="flex-1 border rounded p-2"
              />
              <button
                onClick={handleUploadPhotos}
                disabled={selectedFiles.length === 0 || uploadingPhotos}
                className="flex items-center px-4 py-2 text-white rounded disabled:bg-gray-400"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(176, 130, 76)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(196, 150, 96)'}
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadingPhotos ? 'Upload...' : `Upload ${selectedFiles.length} photo(s)`}
              </button>
            </div>
            {selectedFiles.length > 0 && (
              <p className="text-sm text-gray-600 mt-2">
                Fichiers sélectionnés: {selectedFiles.map(f => f.name).join(', ')}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {selectedAlbum.photos && selectedAlbum.photos.map((photo) => (
            <div key={photo.photo_id} className="relative group">
              <img
                src={`${API_BASE}${photo.photo_url}`}
                alt="Photo"
                className="w-full h-48 object-cover rounded-lg"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center gap-2">
                <button
                  onClick={() => handleSetCover(photo.photo_id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-white p-2 rounded"
                  style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                  title="Définir comme couverture"
                >
                  {selectedAlbum.photo_couverture_id === photo.photo_id ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Image className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={() => handleDeletePhoto(photo.photo_id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 text-white p-2 rounded"
                  title="Supprimer"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
              {selectedAlbum.photo_couverture_id === photo.photo_id && (
                <div className="absolute top-2 right-2 text-white text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgb(196, 150, 96)' }}>
                  Couverture
                </div>
              )}
            </div>
          ))}
        </div>

        {(!selectedAlbum.photos || selectedAlbum.photos.length === 0) && (
          <div className="text-center py-12 text-gray-500">
            <Image className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>Aucune photo dans cet album</p>
          </div>
        )}
      </div>
    );
  }

  // Vue liste des albums
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Gestion des Albums Photos</h1>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center px-4 py-2 text-white rounded"
          style={{ backgroundColor: 'rgb(196, 150, 96)' }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(176, 130, 76)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(196, 150, 96)'}
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouvel album
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Créer un nouvel album</h2>
          <form onSubmit={handleCreateAlbum} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Titre de l'album *</label>
              <input
                type="text"
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                required
                className="w-full border rounded p-2"
                placeholder="Ex: Sortie à Bruges 2024"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Date de l'album *</label>
              <input
                type="date"
                value={formData.date_album}
                onChange={(e) => setFormData({ ...formData, date_album: e.target.value })}
                required
                className="w-full border rounded p-2"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 text-white rounded"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(176, 130, 76)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(196, 150, 96)'}
              >
                Créer l'album
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {albums.map((album) => {
          const coverUrl = album.photo_couverture_id 
            ? `/api/files/${album.photo_couverture_id}`
            : (album.photos && album.photos[0] ? album.photos[0].photo_url : null);

          return (
            <div key={album.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div 
                onClick={() => fetchAlbumDetails(album.id)}
                className="cursor-pointer"
              >
                {coverUrl ? (
                  <img
                    src={`${API_BASE}${coverUrl}`}
                    alt={album.titre}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                    <Image className="w-12 h-12 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-1">{album.titre}</h3>
                <p className="text-sm text-gray-600 mb-2">
                  <Calendar className="inline w-4 h-4 mr-1" />
                  {new Date(album.date_album).toLocaleDateString('fr-FR')}
                </p>
                <p className="text-sm text-gray-500">
                  {album.photos?.length || 0} photo(s)
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => fetchAlbumDetails(album.id)}
                    className="flex-1 flex items-center justify-center px-3 py-2 text-white rounded text-sm"
                    style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(176, 130, 76)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(196, 150, 96)'}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Gérer
                  </button>
                  {album.photos?.length > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadAlbum(album.id, album.titre);
                      }}
                      disabled={downloadingAlbum === album.id}
                      className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                      title="Télécharger toutes les photos"
                    >
                      {downloadingAlbum === album.id ? (
                        <span className="animate-spin">⏳</span>
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteAlbum(album.id)}
                    className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {albums.length === 0 && !showCreateForm && (
        <div className="text-center py-12 text-gray-500">
          <Image className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Aucun album pour le moment</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="mt-4 hover:underline"
            style={{ color: 'rgb(196, 150, 96)' }}
          >
            Créer votre premier album
          </button>
        </div>
      )}
    </div>
  );
};

export default AlbumsManagement;
